import { useState } from "react";
import { useSelector } from "react-redux";
import { Button, Box, Typography } from "@mui/material";

import { RootState } from "../../state";

import { UserData } from "../../types/authTypes";

import UserService from "../../services/userService";
import LoginService from "../../services/loginService";

import TextfieldUpdater from "../../components/TextfieldUpdater/TextfieldUpdater";
import DatePickerUpdater from "../../components/DatePickerUpdater/DatePickerUpdater";
import CountryPickerUpdater from "../../components/CountryPickerUpdater/CountryPickerUpdater";
import NotificationService from "../../components/Notification/notificationService";

enum LABELS {
  TITLE = "My details",
  FIRST_NAME = "First name",
  LAST_NAME = "Last Name",
  DOB = "Date of birth",
  COMPANY = "Company",
  COUNTRY = "Country",
  ADDRESS_LINE_1 = "Address line 1",
  ADDRESS_LINE_2 = "Address line 2",
  CITY = "City",
  PHONE_NUMBER = "Phone Number",
}

const MyDetails = () => {
  const loginData = useSelector((state: RootState) => state.login);

  const [userData, setUserData] = useState<UserData | undefined>(
    loginData.userData
  );

  const { error, success } = NotificationService();
  const { updateMyDetails } = UserService();
  const { saveUser } = LoginService();

  const setFirstName = (value: string) => {
    if (userData) {
      setUserData({ ...userData, firstname: value });
    }
  };

  const setLastName = (value: string) => {
    if (userData) {
      setUserData({ ...userData, lastname: value });
    }
  };

  const setDob = (value: string) => {
    if (userData) {
      setUserData({ ...userData, birthdate: value });
    }
  };

  const setCompany = (value: string) => {
    if (userData) {
      setUserData({ ...userData, company: value });
    }
  };

  const setCountry = (value: string) => {
    if (userData) {
      setUserData({ ...userData, country: value });
    }
  };

  const setAddressLine1 = (value: string) => {
    if (userData) {
      setUserData({ ...userData, address_1: value });
    }
  };

  const setAddressLine2 = (value: string) => {
    if (userData) {
      setUserData({ ...userData, address_2: value });
    }
  };

  const setCity = (value: string) => {
    if (userData) {
      setUserData({ ...userData, city: value });
    }
  };

  const setPhoneNumber = (value: string) => {
    if (userData) {
      setUserData({ ...userData, telephone: value });
    }
  };

  const save = () => {
    if (userData) {
      let newValues: any = {};
      let updated = false;
      Object.keys(userData).forEach((key) => {
        if (
          loginData?.userData &&
          userData[key] &&
          userData[key] !== loginData.userData[key]
        ) {
          if (typeof userData[key] === "string") {
            newValues[key] = userData[key];
            updated = true;
          }
        }
      });

      if (updated) {
        updateMyDetails(newValues).then((result) => {
          if (result) {
            const newUserData = { ...userData };
            saveUser({ ...loginData, userData: newUserData });
            success("User details updated");
          } else {
            error("User details update error");
          }
        });
      } else {
        error("No changes detected");
      }
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        padding: { xs: "2.5rem 1rem", sm: "2.5rem 2rem" },
        borderRadius: "10px",
        backgroundColor: "white !important",
      }}
    >
      <Typography
        variant="followCardUserName"
        sx={{ fontSize: "1.5rem !important", marginBottom: "0.5rem" }}
      >
        {LABELS.TITLE}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          rowGap: 2,
        }}
      >
        <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
          <TextfieldUpdater
            label={LABELS.FIRST_NAME}
            value={userData?.firstname}
            setValue={setFirstName}
          />
        </Box>
        <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
          <TextfieldUpdater
            label={LABELS.LAST_NAME}
            value={userData?.lastname}
            setValue={setLastName}
          />
        </Box>
        <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
          <DatePickerUpdater
            label={LABELS.DOB}
            value={userData?.birthdate}
            setValue={setDob}
          />
        </Box>
        <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
          <TextfieldUpdater
            label={LABELS.PHONE_NUMBER}
            value={userData?.telephone}
            setValue={setPhoneNumber}
          />
        </Box>
        <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
          <TextfieldUpdater
            label={LABELS.ADDRESS_LINE_1}
            value={userData?.address_1}
            setValue={setAddressLine1}
          />
        </Box>
        <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
          <TextfieldUpdater
            label={LABELS.ADDRESS_LINE_2}
            value={userData?.address_2}
            setValue={setAddressLine2}
          />
        </Box>
        <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
          <TextfieldUpdater
            label={LABELS.CITY}
            value={userData?.city}
            setValue={setCity}
          />
        </Box>
        <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
          <CountryPickerUpdater
            label={LABELS.COUNTRY}
            value={userData?.country}
            setValue={setCountry}
          />
        </Box>
        <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
          <TextfieldUpdater
            label={LABELS.COMPANY}
            value={userData?.company}
            setValue={setCompany}
          />
        </Box>
        <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
              height: "100%",
            }}
          >
            <Button
              variant="darkBlue"
              sx={{
                height: "50px !important",
                textTransform: "uppercase",
              }}
              onClick={save}
            >
              Save changes
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MyDetails;
